// src/pages/TeamPerformance.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useData } from "../context/DataContext";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Pie, Bar } from "react-chartjs-2";
import {
  Box,
  Autocomplete,
  TextField,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Trend from "components/Trend";
import { rugbyFlagPath, FlagImg } from "../utils/flags";
import HelpIcon from "../components/shared/HelpIcon";
import OpponentsTable, { OpponentRow } from "../components/performance/OpponentsTable";
import FancyTopList from "../components/performance/FancyTopList";

ChartJS.register(
  ArcElement,
  ChartTooltip,
  ChartLegend,
  CategoryScale,
  LinearScale,
  BarElement
);

interface Match {
  date: string;
  year: number;
  home_team: string;
  away_team: string;
  home_score: number;
  away_score: number;
  home_tries?: number;
  away_tries?: number;
}

// =======================
// Color & style tokens
// =======================
const palette = {
  bg: "#EEF5FF",
  panel: "#B4D4FF",
  card: "#FFFFFF",
  border: "#86B6F6",
  ink: "#0B2230",
  soft: "rgba(23,107,135,0.08)",
  accent: "#176B87",
};

const boxShadow =
  "0 14px 30px rgba(23,107,135,0.14), 0 2px 10px rgba(0,0,0,0.04)";

// =======================
// Helpers
// =======================
const toNum = (v: any, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const toYear = (dateStr: string) => {
  const d = new Date(String(dateStr).trim());
  const y = d.getFullYear();
  return Number.isFinite(y) ? y : NaN;
};

const FlagHero: React.FC<{ team?: string; maxWidth?: number }> = ({
  team,
  maxWidth = 560,
}) => {
  const src = rugbyFlagPath(team);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "grid",
        placeItems: "center",
      }}
      aria-label={`${team || "Team"} flag`}
    >
      <img
        src={src}
        alt={`${team || "Team"} flag`}
        style={{
          width: `min(100%, ${maxWidth}px)`,
          height: "auto",
          display: "block",
          objectFit: "contain",
          imageRendering: "auto",
        }}
      />
    </div>
  );
};

const TeamPerformance: React.FC = () => {
  const { rows: rawRows, loading } = useData();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [teamInput, setTeamInput] = useState("");
  const [team, setTeam] = useState<string>("");

  const matches = useMemo<Match[]>(() => {
    return rawRows
      .map((r) => {
        const year = toYear(r.date);
        const ht = (r as any).home_tries ?? (r as any).home_try ?? (r as any).ht;
        const at = (r as any).away_tries ?? (r as any).away_try ?? (r as any).at;
        return {
          date: String(r.date).trim(),
          year,
          home_team: String(r.home_team).trim(),
          away_team: String(r.away_team).trim(),
          home_score: toNum(r.home_score),
          away_score: toNum(r.away_score),
          home_tries: ht !== undefined ? toNum(ht, 0) : undefined,
          away_tries: at !== undefined ? toNum(at, 0) : undefined,
        };
      })
      .filter((m) => Number.isFinite(m.year));
  }, [rawRows]);

  const allTeams = useMemo<string[]>(() => {
    const s = new Set<string>();
    matches.forEach((m) => { s.add(m.home_team); s.add(m.away_team); });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [matches]);

  useEffect(() => {
    if (!team && allTeams.length) {
      setTeam(allTeams[0]);
      setTeamInput(allTeams[0]);
    }
  }, [allTeams, team]);

  const triesAvailable = useMemo(
    () =>
      matches.some(
        (m) =>
          typeof m.home_tries === "number" || typeof m.away_tries === "number"
      ),
    [matches]
  );

  type OppRow = {
    opponent: string;
    played: number;
    wins: number;
    losses: number;
    draws: number;
    pf: number;
    pa: number;
  };

  const { teamTotals, byOpponent } = useMemo(() => {
    if (!team) {
      return {
        teamTotals: {
          total: 0,
          wins: 0,
          losses: 0,
          draws: 0,
          homeWinPct: 0,
          awayWinPct: 0,
          totalTries: null as number | null,
        },
        byOpponent: [] as OppRow[],
      };
    }

    const games = matches.filter(
      (m) => m.home_team === team || m.away_team === team
    );

    let wins = 0;
    let losses = 0;
    let draws = 0;
    let homeMatches = 0;
    let awayMatches = 0;
    let homeWins = 0;
    let awayWins = 0;
    let tries = 0;
    let hasAnyTries = false;

    const map = new Map<string, OppRow>();

    games.forEach((m) => {
      const isHome = m.home_team === team;
      const us = isHome ? m.home_score : m.away_score;
      const them = isHome ? m.away_score : m.home_score;
      const opp = isHome ? m.away_team : m.home_team;

      if (us > them) wins++;
      else if (us < them) losses++;
      else draws++;

      if (isHome) {
        homeMatches++;
        if (us > them) homeWins++;
        if (typeof m.home_tries === "number") {
          tries += m.home_tries;
          hasAnyTries = true;
        }
      } else {
        awayMatches++;
        if (us > them) awayWins++;
        if (typeof m.away_tries === "number") {
          tries += m.away_tries;
          hasAnyTries = true;
        }
      }

      const row = map.get(opp) ?? {
        opponent: opp,
        played: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        pf: 0,
        pa: 0,
      };

      row.played += 1;
      row.pf += us;
      row.pa += them;

      if (us > them) row.wins += 1;
      else if (us < them) row.losses += 1;
      else row.draws += 1;

      map.set(opp, row);
    });

    const pct = (num: number, den: number) =>
      den ? Math.round((num / den) * 1000) / 10 : 0;

    return {
      teamTotals: {
        total: games.length,
        wins,
        losses,
        draws,
        homeWinPct: pct(homeWins, homeMatches),
        awayWinPct: pct(awayWins, awayMatches),
        totalTries: triesAvailable ? tries : null,
      },
      byOpponent: Array.from(map.values()),
    };
  }, [team, matches, triesAvailable]);

  const teamStats = teamTotals;

  const gamesByTeamPie = useMemo(() => {
    if (!matches.length) {
      return {
        labels: [] as string[],
        datasets: [
          {
            data: [] as number[],
            backgroundColor: [] as string[],
            borderColor: [] as string[],
            borderWidth: [] as number[],
            offset: [] as number[],
          },
        ],
      };
    }

    const counts: Record<string, number> = {};

    matches.forEach((m) => {
      counts[m.home_team] = (counts[m.home_team] ?? 0) + 1;
      counts[m.away_team] = (counts[m.away_team] ?? 0) + 1;
    });

    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);

    const TOP = 10;
    const top = sorted.slice(0, TOP);
    const other = sorted.slice(TOP);
    const otherSum = other.reduce((s, [, n]) => s + n, 0);

    const labels = top
      .map(([name]) => name)
      .concat(other.length ? ["Other"] : []);

    const data = top.map(([, n]) => n).concat(other.length ? [otherSum] : []);

    const highlightLabel = labels.includes(team)
      ? team
      : labels.includes("Other")
      ? "Other"
      : null;

    const backgroundColor = labels.map((lbl) =>
      lbl === highlightLabel ? palette.accent : "rgba(134,182,246,0.55)"
    );

    const borderColor = labels.map((lbl) =>
      lbl === highlightLabel ? palette.accent : "#ffffff"
    );

    const borderWidth = labels.map((lbl) => (lbl === highlightLabel ? 4 : 2));
    const offset = labels.map((lbl) => (lbl === highlightLabel ? 10 : 0));

    return {
      labels,
      datasets: [
        {
          label: "Games Played (share)",
          data,
          backgroundColor,
          borderColor,
          borderWidth,
          offset,
        },
      ],
    };
  }, [matches, team]);

  const topWins = useMemo(
    () =>
      [...byOpponent]
        .sort((a, b) => b.wins - a.wins)
        .slice(0, 5)
        .map((r, i) => ({ rank: i + 1, name: r.opponent, value: r.wins })),
    [byOpponent]
  );

  const topLosses = useMemo(
    () =>
      [...byOpponent]
        .sort((a, b) => b.losses - a.losses)
        .slice(0, 5)
        .map((r, i) => ({ rank: i + 1, name: r.opponent, value: r.losses })),
    [byOpponent]
  );

  const topAvgPointsFor = useMemo(
    () =>
      [...byOpponent]
        .filter((r) => r.played >= 3)
        .map((r) => ({ ...r, apf: r.pf / r.played }))
        .sort((a, b) => b.apf - a.apf)
        .slice(0, 5)
        .map((r, i) => ({
          rank: i + 1,
          name: r.opponent,
          value: Number(r.apf.toFixed(2)),
        })),
    [byOpponent]
  );

  const tableRows = useMemo(() => {
    return [...byOpponent]
      .map((r) => ({
        ...r,
        gd: r.pf - r.pa,
        winPct: r.played ? Math.round((r.wins / r.played) * 1000) / 10 : 0,
      }))
      .sort((a, b) => b.played - a.played);
  }, [byOpponent]);

  const oppByPCPM = useMemo(() => {
    const arr = [...byOpponent]
      .filter((r) => r.played >= 3)
      .map((r) => ({ name: r.opponent, v: r.pa / r.played }))
      .sort((a, b) => b.v - a.v)
      .slice(0, 10);

    return {
      labels: arr.map((x) => x.name),
      datasets: [
        {
          label: "Pts Conceded / Match",
          data: arr.map((x) => Number(x.v.toFixed(2))),
          backgroundColor: palette.accent,
        },
      ],
    };
  }, [byOpponent]);

  const oppByPSPM = useMemo(() => {
    const arr = [...byOpponent]
      .filter((r) => r.played >= 3)
      .map((r) => ({ name: r.opponent, v: r.pf / r.played }))
      .sort((a, b) => b.v - a.v)
      .slice(0, 10);

    return {
      labels: arr.map((x) => x.name),
      datasets: [
        {
          label: "Pts Scored / Match",
          data: arr.map((x) => Number(x.v.toFixed(2))),
          backgroundColor: "rgba(134,182,246,0.85)",
        },
      ],
    };
  }, [byOpponent]);

  const commitTeam = (candidate: string) => {
    const found = allTeams.find(
      (t) => t.toLowerCase() === candidate.toLowerCase()
    );
    if (found) {
      setTeam(found);
      setTeamInput(found);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: palette.bg, padding: 16 }}>
        <Box sx={{ maxWidth: 900, mx: "auto", pt: 2 }}>
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="rectangular" height={340} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
        </Box>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: palette.bg,
        color: palette.ink,
        padding: 16,
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1300,
          background: palette.bg,
          py: 1.5,
          mb: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 520,
            background: palette.panel,
            border: `1px solid ${palette.border}`,
            borderRadius: 2,
            p: 1,
            boxShadow,
          }}
        >
          <Autocomplete
            options={allTeams}
            freeSolo
            value={team}
            inputValue={teamInput}
            onInputChange={(_, v) => setTeamInput(v)}
            onChange={(_, v) => {
              if (typeof v === "string") {
                commitTeam(v);
              } else if (v) {
                commitTeam(v);
              }
            }}
            onBlur={() => commitTeam(teamInput.trim())}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitTeam(teamInput.trim());
              }
            }}
            renderOption={(props, option) => (
              <li
                {...props}
                key={option}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <FlagImg name={option} />
                <span>{option}</span>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Change Team"
                placeholder="Start typing a team…"
                variant="outlined"
                size="medium"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
                      <FlagImg name={team} />
                    </Box>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    borderRadius: 2,
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(11,34,48,0.7)",
                  },
                }}
              />
            )}
            PaperComponent={(props) => (
              <Box
                component="ul"
                {...props}
                sx={{
                  bgcolor: "#fff",
                  border: `1px solid ${palette.border}`,
                  borderRadius: 2,
                  boxShadow: "0 14px 30px rgba(23,107,135,0.14)",
                  overflow: "hidden",
                }}
              />
            )}
            ListboxProps={{
              sx: {
                "& li": { px: 1.5, py: 1 },
                "& li.Mui-focused": { background: palette.soft },
              },
            }}
          />
        </Box>
      </Box>

      <div
        style={{
          background: palette.panel,
          padding: 12,
          borderRadius: 14,
          border: `1px solid ${palette.border}`,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            justifyContent: "center",
            background: palette.card,
            border: `1px solid ${palette.border}`,
            borderRadius: 14,
            padding: "12px 16px",
            fontWeight: 900,
            marginBottom: 12,
            textAlign: "center",
            boxShadow,
          }}
        >
          <FlagImg name={team} size={24} />
          <span>{team || "Team Name"}</span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
            gap: 16,
            marginBottom: 12,
          }}
        >
          {[
            { label: "Win", value: teamStats.wins },
            { label: "Loss", value: teamStats.losses },
            { label: "Home Win %", value: `${teamStats.homeWinPct}%` },
            { label: "Away Win %", value: `${teamStats.awayWinPct}%` },
          ].map((k) => (
            <div
              key={k.label}
              style={{
                background: palette.card,
                border: `1px solid ${palette.border}`,
                borderRadius: 14,
                padding: 16,
                textAlign: "center",
                fontWeight: 800,
                boxShadow,
              }}
            >
              <div style={{ opacity: 0.8 }}>{k.label}</div>
              <div style={{ fontSize: 22, marginTop: 6 }}>{k.value}</div>
            </div>
          ))}
        </div>

        <div
          style={{
            background: palette.card,
            border: `1px solid ${palette.border}`,
            borderRadius: 14,
            padding: 16,
            boxShadow,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              alignItems: "stretch",
            }}
          >
            <div style={{ minHeight: 340, display: "grid", alignItems: "center" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 950 }}>Games Share (Top Teams)</div>
                <HelpIcon
                  title="Pie chart meaning"
                  body={
                    <>
                      <div>
                        Shows which teams appear most often in the dataset (as home
                        or away).
                      </div>
                      <div style={{ marginTop: 6 }}>
                        The selected team is highlighted (or “Other” if it’s outside
                        top-10).
                      </div>
                    </>
                  }
                />
              </div>

              <Pie
                data={gamesByTeamPie}
                options={{
                  plugins: {
                    legend: { position: "bottom" },
                    tooltip: { enabled: true },
                  },
                  animation: { animateRotate: true, duration: 500 },
                }}
              />
            </div>

            <div
              style={{
                minHeight: 340,
                background: "transparent",
                border: "none",
                borderRadius: 12,
                padding: 0,
                display: "grid",
              }}
            >
              <FlagHero team={team} maxWidth={560} />
            </div>
          </div>

          <div
            style={{
              marginTop: 14,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 6 }}>
                Total Games Played
              </div>
              <div
                style={{
                  fontSize: 46,
                  fontWeight: 950,
                  lineHeight: 1,
                  color: palette.accent,
                }}
              >
                {teamStats.total}
              </div>
            </div>

            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 6 }}>
                {triesAvailable ? "Total Tries Scored" : "Total Points Scored"}
              </div>
              <div
                style={{
                  fontSize: 46,
                  fontWeight: 950,
                  lineHeight: 1,
                  color: palette.accent,
                }}
              >
                {teamStats.totalTries === null ? "N/A" : teamStats.totalTries}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          background: palette.panel,
          padding: 12,
          borderRadius: 14,
          border: `1px solid ${palette.border}`,
          marginTop: 16,
        }}
      >
        <div
          style={{
            background: palette.card,
            border: `1px solid ${palette.border}`,
            borderRadius: 14,
            padding: 18,
            boxShadow,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 14,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                background: "rgba(180,212,255,0.55)",
                border: `1px solid ${palette.border}`,
                borderRadius: 10,
                padding: "6px 10px",
                fontWeight: 900,
                color: palette.ink,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Top Performers — Teams
              <HelpIcon
                title="What this section means"
                body={
                  <>
                    <div>
                      These cards summarize your selected team’s performance against
                      opponents.
                    </div>
                    <div style={{ marginTop: 6 }}>
                      It’s computed from matches where the selected team appears as
                      home or away.
                    </div>
                  </>
                }
              />
            </div>
            <div style={{ opacity: 0.7, fontSize: 12 }}>
              Based on opponent aggregates for the selected team
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 16,
            }}
          >
            <FancyTopList
              title="Most Wins vs Opponent"
              items={topWins}
              unit={`${team} wins`}
            />
            <FancyTopList
              title="Most Losses vs Opponent"
              items={topLosses}
              unit={`${team} losses`}
            />
            <FancyTopList
              title="Highest Avg Points Scored"
              items={topAvgPointsFor}
              unit="pts/match"
            />
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 16,
          background: palette.panel,
          border: `1px solid ${palette.border}`,
          borderRadius: 14,
          padding: 12,
        }}
      >
        <div
          style={{
            background: palette.card,
            border: `1px solid ${palette.border}`,
            borderRadius: 14,
            padding: 12,
            boxShadow,
          }}
        >
          <div
            style={{
              textAlign: "center",
              padding: 6,
              fontWeight: 900,
              color: palette.ink,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            League Statistics
            <HelpIcon
              title="What is ‘League Statistics’ here?"
              body={
                <>
                  <div>
                    This section combines a detailed opponent table + opponent
                    difficulty charts.
                  </div>
                  <div style={{ marginTop: 6 }}>
                    Think of it as: “How does this team perform against specific
                    opponents?”
                  </div>
                </>
              }
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "auto auto",
              gap: 12,
              marginTop: 8,
            }}
          >
            {/* Opponent Table */}
            <div
              style={{
                background: "#fff",
                border: `1px solid ${palette.border}`,
                borderRadius: 14,
                padding: 12,
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  marginBottom: 8,
                  color: palette.ink,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Opponent Table
                <HelpIcon
                  title="How to read this table"
                  body={
                    <>
                      <div>
                        Each row is one opponent. The stats summarize all matches vs
                        that opponent.
                      </div>
                      <div style={{ marginTop: 6 }}>
                        Hover the ⓘ icons in the header to learn what PF/PA/GD etc.
                        mean.
                      </div>
                    </>
                  }
                />
              </div>

              <OpponentsTable rows={tableRows as OpponentRow[]} />
            </div>

            {/* Opponents by Points Conceded per Match */}
            <div
              style={{
                background: "#fff",
                border: `1px solid ${palette.border}`,
                borderRadius: 14,
                padding: 12,
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  marginBottom: 8,
                  color: palette.ink,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Opponents by Points Conceded per Match
                <HelpIcon
                  title="What this chart means"
                  body={
                    <>
                      <div>
                        For opponents with at least <b>3 games played</b>, this ranks
                        who scores the most against the selected team per match.
                      </div>
                      <div style={{ marginTop: 6 }}>
                        Higher bars = tougher opponents defensively (you concede
                        more).
                      </div>
                    </>
                  }
                />
              </div>

              <Bar
                data={oppByPCPM}
                options={{
                  indexAxis: "y",
                  plugins: { legend: { display: false } },
                  scales: {
                    x: {
                      ticks: { color: palette.ink },
                      grid: { color: "rgba(134,182,246,0.45)" },
                    },
                    y: {
                      ticks: { color: palette.ink },
                      grid: { color: "transparent" },
                    },
                  },
                }}
              />
            </div>

            {/* Matches per Year — Trend */}
            <div
              style={{
                background: "#fff",
                border: `1px solid ${palette.border}`,
                borderRadius: 14,
                padding: 12,
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  marginBottom: 8,
                  color: palette.ink,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Matches per Year Trend
                <HelpIcon
                  title="What this chart means"
                  body={
                    <>
                      <div>Shows how many matches exist per year in the dataset.</div>
                      <div style={{ marginTop: 6 }}>
                        Spikes can mean better coverage / more recorded competitions,
                        not necessarily “stronger teams”.
                      </div>
                    </>
                  }
                />
              </div>

              <Trend embed height={260} />
            </div>

            {/* Opponents by Points Scored per Match */}
            <div
              style={{
                background: "#fff",
                border: `1px solid ${palette.border}`,
                borderRadius: 14,
                padding: 12,
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  marginBottom: 8,
                  color: palette.ink,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Opponents by Points Scored per Match
                <HelpIcon
                  title="What this chart means"
                  body={
                    <>
                      <div>
                        For opponents with at least <b>3 games played</b>, this ranks
                        where you score the most points per match.
                      </div>
                      <div style={{ marginTop: 6 }}>
                        Higher bars = opponents you tend to attack well against.
                      </div>
                    </>
                  }
                />
              </div>

              <Bar
                data={oppByPSPM}
                options={{
                  indexAxis: "y",
                  plugins: { legend: { display: false } },
                  scales: {
                    x: {
                      ticks: { color: palette.ink },
                      grid: { color: "rgba(134,182,246,0.45)" },
                    },
                    y: {
                      ticks: { color: palette.ink },
                      grid: { color: "transparent" },
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamPerformance;